/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { type ReportsRecord, ReportKinds } from '@karya/core';
import { createApi } from '@reduxjs/toolkit/query/react';
import { apiUserBaseQuery } from './apiSlice';

/**
 * Origin marker used by reportsApi routes to choose web/mobile endpoint variants.
 */
export type RequestSource = 'mobile' | 'web';

export type GraphType = 'bar' | 'filtered-bar' | 'filtered-grouped-bar';

type ReportByKindAndIdRequest = {
  id: string;
  src: RequestSource;
};

/**
 * ReportQueryParameter describes one runtime query input for a report:
 * - `name`: parameter identifier used in the SQL/template
 * - `type`: parameter kind/operator understood by report UIs/backends
 * - `value`: optional user-provided payload for execution
 * - `comments`: optional UI hint/description
 */
export type ReportQueryParameter = {
  name: string;
  type: string;
  comments?: string;
  value?: unknown;
};

/**
 * Request shape used by getReportResult for paginated data retrieval.
 */
type ReportResultRequest = {
  id: string;
  limit: number;
  page: number;
  params: string[];
  searchQuery?: Record<string, string>;
  src: RequestSource;
  forceRefresh?: boolean;
};

/**
 * Response shape returned by getReportResult.
 */
type ReportResultResponse = {
  meta: {
    columns: string[];
    kind: ReportKind;
    id: string;
  };
  data: Record<string, unknown>[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

/**
 * Request shape used by getReportDownloadUrl.
 */
type ReportDownloadUrlRequest = {
  id: string;
  params: string[];
  searchQuery?: Record<string, string>;
  src: RequestSource;
  mediaEnabled?: boolean;
  forceRefresh?: boolean;
};

type GraphResultRequest = {
  collectionId: string;
};

type GraphResultResponse = {
  data: {
    id: string;
    name: string;
    type: GraphType;
    data: unknown[];
    yLabel: string;
  }[];
};

const _reportKinds = ReportKinds.filter((x) => x !== 'graphs');
/**
 * Canonical report kinds accepted by report endpoints (graphs excluded).
 */
export type ReportKind = (typeof _reportKinds)[number];

/**
 * All API routes for reports
 */
export const reportsApi = createApi({
  reducerPath: 'reportsApi',

  baseQuery: apiUserBaseQuery,

  refetchOnReconnect: true,

  tagTypes: ['AllReports', 'ReportById'],

  endpoints: (builder) => ({
    /**
     * Report endpoints
     */
    getAllReports: builder.query<
      ReportsRecord[],
      { src: RequestSource; project_id: string | undefined; kind?: ReportKind }
    >({
      query: ({ src, project_id, kind }) => ({
        url: src === 'mobile' ? `/mobile/reports/project/${project_id}` : '/reports/',
        method: 'GET',
        params: src === 'mobile' ? undefined : kind ? { kind } : undefined,
        headers: src === 'mobile' ? { src } : { src, project_id },
      }),
      providesTags: (_response, _error, { project_id }) => [{ type: 'AllReports', id: project_id }],
    }),

    getReportById: builder.query<ReportsRecord, ReportByKindAndIdRequest>({
      query: ({ id, src }) => {
        const url = src === 'mobile' ? `/mobile/reports/${id}` : `/reports/${id}`;
        return {
          url,
          method: 'GET',
          headers: {
            src,
          },
        };
      },
      providesTags: (_response, _error, { id }) => [{ type: 'ReportById', id }],
    }),

    getReportResult: builder.query<ReportResultResponse, ReportResultRequest>({
      query: (req) => {
        const { limit, page, params, id, src, searchQuery, forceRefresh } = req;
        const encodedParams = encodeURIComponent(JSON.stringify(params));
        let url = `/reports/${id}/result?limit=${limit}&page=${page}&params=${encodedParams}`;
        if (searchQuery) url += `&searchQuery=${encodeURIComponent(JSON.stringify(searchQuery))}`;
        if (forceRefresh) url += '&forceRefresh=true';
        return {
          url: src === 'mobile' ? `/mobile${url}` : url,
          method: 'GET',
        };
      },
    }),

    getReportDownloadUrl: builder.query<{ url: string }, ReportDownloadUrlRequest>({
      query: (req) => {
        const { params, id, src, mediaEnabled, searchQuery, forceRefresh } = req;
        const encodedParams = encodeURIComponent(JSON.stringify(params));
        let url = `/reports/${id}/download?params=${encodedParams}&mediaEnabled=${mediaEnabled || false}`;
        if (searchQuery) url += `&searchQuery=${encodeURIComponent(JSON.stringify(searchQuery))}`;
        if (forceRefresh) url += '&forceRefresh=true';
        return {
          url: src === 'mobile' ? `/mobile${url}` : url,
          method: 'GET',
        };
      },
    }),

    createReport: builder.mutation<
      ReportsRecord,
      { src: RequestSource; body: Omit<ReportsRecord, 'id'> & Partial<Pick<ReportsRecord, 'id'>> }
    >({
      query: ({ src, body }) => ({
        url: src === 'mobile' ? '/mobile/reports' : '/reports',
        method: 'POST',
        body,
        headers: { src },
      }),
      // TODO: Invalidate the report by project_id. Requires passing project_id to the query.
      invalidatesTags: [{ type: 'AllReports' }],
    }),

    updateReport: builder.mutation<ReportsRecord, { src: RequestSource; id: string; body: Partial<ReportsRecord> }>({
      query: ({ src, id, body }) => ({
        url: src === 'mobile' ? `/mobile/reports/${id}` : `/reports/${id}`,
        method: 'PUT',
        body,
        headers: { src },
      }),
      invalidatesTags: (_response, _error, { id }) => [{ type: 'ReportById', id }],
    }),

    deleteReport: builder.mutation<{ message: string }, { src: RequestSource; id: string }>({
      query: ({ src, id }) => ({
        url: src === 'mobile' ? `/mobile/reports/${id}` : `/reports/${id}`,
        method: 'DELETE',
        headers: { src },
      }),
      invalidatesTags: (_response, _error, { id }) => [{ type: 'ReportById', id }, { type: 'AllReports' }],
    }),

    // Graph/collection APIs are intentionally disabled until backend support is reintroduced.
    getAllGraphCollections: builder.query<Record<string, never>, { src: RequestSource }>({
      queryFn: () =>
        Promise.resolve({
          error: {
            status: 501,
            data: 'Graph collections endpoint is disabled',
          },
        }),
    }),

    // Graph/collection APIs are intentionally disabled until backend support is reintroduced.
    getCollectionResult: builder.query<GraphResultResponse, GraphResultRequest>({
      queryFn: () =>
        Promise.resolve({
          error: {
            status: 501,
            data: 'Graph collection result endpoint is disabled',
          },
        }),
    }),
  }),
});
